import React, { useState } from 'react';

interface ToolScore {
  source_id: string;
  name: string;
  score: number;
  explanation: string;
  pricing_model: string;
  entry_price?: number;
  cost_per_lead?: number;
  features: Record<string, unknown>;
  requires_known_domain?: boolean;
  url?: string;
}

interface ComparisonMatrixProps {
  results: ToolScore[];
  onSelectTool: (tool: ToolScore) => void;
  loading?: boolean;
}

type SortField = 'score' | 'price';

// ── Score Ring ────────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 52 }: { score: number; size?: number }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(score, 100) / 100;
  const color =
    score >= 75 ? '#2A7D5F' : score >= 55 ? '#8A3033' : 'rgba(45,49,66,0.5)';

  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke="#e5e7eb" strokeWidth={5}
      />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={color} strokeWidth={5}
        strokeDasharray={`${circ * pct} ${circ * (1 - pct)}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dasharray 0.8s ease' }}
      />
      <text
        x={size / 2} y={size / 2 + 4}
        textAnchor="middle"
        fontSize={11}
        fontWeight={700}
        fill={color}
        fontFamily="Inter, sans-serif"
      >
        {Math.round(score)}
      </text>
    </svg>
  );
}

// ── Feature Tag ───────────────────────────────────────────────────────────────
interface TagDef {
  key: string;
  label: string;
  variant: 'green' | 'amber' | 'crimson' | 'navy';
}

const TAG_DEFINITIONS: TagDef[] = [
  { key: 'cold_discovery',       label: 'Live search',      variant: 'green'   },
  { key: 'requires_known_domain', label: 'Needs domain',    variant: 'amber'   },
  { key: 'manual',               label: 'Manual',           variant: 'amber'   },
  { key: 'decision_makers',      label: 'Decision makers',  variant: 'crimson' },
  { key: 'email_verification',   label: 'Email verified',   variant: 'green'   },
  { key: 'verified_emails',      label: 'Email verified',   variant: 'green'   },
  { key: 'enterprise_focus',     label: 'Enterprise',       variant: 'navy'    },
  { key: 'local_focus',          label: 'Local focus',      variant: 'crimson' },
  { key: 'fresh_data',           label: 'Fresh data',       variant: 'green'   },
];

const TAG_STYLES: Record<TagDef['variant'], { bg: string; text: string }> = {
  green:   { bg: '#EEF7F3', text: '#2A7D5F' },
  amber:   { bg: '#FEF4E8', text: '#B86B1A' },
  crimson: { bg: '#FDF0F0', text: '#8A3033' },
  navy:    { bg: 'rgba(45,49,66,0.08)', text: '#2D3142' },
};

function FeatureTag({ label, variant }: { label: string; variant: TagDef['variant'] }) {
  const s = TAG_STYLES[variant];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 600,
        background: s.bg,
        color: s.text,
        letterSpacing: '0.01em',
      }}
    >
      {label}
    </span>
  );
}

function getToolTags(features: Record<string, unknown>): TagDef[] {
  const seen = new Set<string>();
  const tags: TagDef[] = [];
  for (const def of TAG_DEFINITIONS) {
    if (tags.length >= 3) break;
    if (features[def.key] && !seen.has(def.label)) {
      seen.add(def.label);
      tags.push(def);
    }
  }
  return tags;
}

// ── Tool Card ─────────────────────────────────────────────────────────────────
function ToolCard({
  tool,
  rank,
  onSelect,
}: {
  tool: ToolScore;
  rank: number;
  onSelect: (tool: ToolScore) => void;
}) {
  const isWebleads = tool.name === 'WebLeads';
  const isTopMatch = rank === 1;
  const tags = getToolTags(tool.features);
  const priceLabel =
    tool.entry_price === 0
      ? 'Free'
      : tool.entry_price
      ? `$${tool.entry_price}/mo`
      : 'Custom';

  const borderColor = isWebleads
    ? '#8A3033'
    : isTopMatch
    ? 'rgba(42,125,95,0.35)'
    : '#e5e7eb';
  const boxShadow = isWebleads ? '0 4px 20px rgba(138,48,51,0.1)' : 'none';

  return (
    <div
      style={{
        background: '#fff',
        border: `1.5px solid ${borderColor}`,
        borderRadius: 16,
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        boxShadow,
        transition: 'box-shadow 0.2s, transform 0.2s',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow =
          '0 6px 24px rgba(45,49,66,0.1)';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = boxShadow;
        (e.currentTarget as HTMLDivElement).style.transform = '';
      }}
    >
      {/* Rank badge */}
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background:
            rank === 1 ? '#8A3033' : rank === 2 ? 'rgba(45,49,66,0.12)' : '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          color: rank === 1 ? '#fff' : '#6b7280',
          flexShrink: 0,
        }}
      >
        #{rank}
      </div>

      {/* Score ring */}
      <ScoreRing score={tool.score} size={52} />

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#2D3142' }}>{tool.name}</span>
          {isWebleads && (
            <span
              style={{
                padding: '2px 8px',
                borderRadius: 99,
                background: '#8A3033',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              BEST VALUE
            </span>
          )}
          {isTopMatch && !isWebleads && (
            <span
              style={{
                padding: '2px 8px',
                borderRadius: 99,
                background: '#EEF7F3',
                color: '#2A7D5F',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              TOP MATCH
            </span>
          )}
        </div>

        {tool.explanation && (
          <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5, marginBottom: 8 }}>
            {tool.explanation}
          </p>
        )}

        {tags.length > 0 && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {tags.map((tag) => (
              <FeatureTag key={tag.key} label={tag.label} variant={tag.variant} />
            ))}
          </div>
        )}
      </div>

      {/* Price + CTA */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <p style={{ fontWeight: 700, fontSize: 17, color: '#2D3142', marginBottom: 8 }}>
          {priceLabel}
        </p>
        <button
          onClick={() => onSelect(tool)}
          style={{
            display: 'inline-block',
            padding: '8px 20px',
            borderRadius: 8,
            background: isWebleads ? '#8A3033' : '#f9f5f5',
            border: '1.5px solid #e5e7eb',
            color: isWebleads ? '#fff' : '#8A3033',
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = '#8A3033';
            (e.currentTarget as HTMLButtonElement).style.color = '#fff';
            (e.currentTarget as HTMLButtonElement).style.borderColor = '#8A3033';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = isWebleads ? '#8A3033' : '#f9f5f5';
            (e.currentTarget as HTMLButtonElement).style.color = isWebleads ? '#fff' : '#8A3033';
            (e.currentTarget as HTMLButtonElement).style.borderColor = '#e5e7eb';
          }}
        >
          {isWebleads ? 'Try Free →' : 'View →'}
        </button>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export const ComparisonMatrix: React.FC<ComparisonMatrixProps> = ({
  results,
  onSelectTool,
  loading = false,
}) => {
  const [sortField, setSortField] = useState<SortField>('score');
  const [showAll, setShowAll] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600 text-sm">Scoring tools for you...</p>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No results found. Try adjusting your criteria.</p>
      </div>
    );
  }

  const sorted = [...results].sort((a, b) => {
    if (sortField === 'score') return b.score - a.score;
    // Free (0) first, then ascending price, then Custom (null/undefined) last
    const aPrice = a.entry_price == null ? Infinity : a.entry_price;
    const bPrice = b.entry_price == null ? Infinity : b.entry_price;
    return aPrice - bPrice;
  });

  const topThree = sorted.slice(0, 3);
  const rest = sorted.slice(3);

  return (
    <div>
      {/* Sort controls */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {(['score', 'price'] as SortField[]).map((field) => (
          <button
            key={field}
            onClick={() => setSortField(field)}
            style={{
              padding: '7px 16px',
              borderRadius: 8,
              border: `1.5px solid ${sortField === field ? '#8A3033' : '#e5e7eb'}`,
              background: sortField === field ? '#FDF0F0' : '#fff',
              color: sortField === field ? '#8A3033' : '#2D3142',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {field === 'score' ? 'Best Match' : 'Lowest Price'}
          </button>
        ))}
      </div>

      {/* Top 3 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
        {topThree.map((tool, i) => (
          <ToolCard key={tool.source_id} tool={tool} rank={i + 1} onSelect={onSelectTool} />
        ))}
      </div>

      {/* Rest */}
      {showAll && rest.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
          {rest.map((tool, i) => (
            <ToolCard key={tool.source_id} tool={tool} rank={i + 4} onSelect={onSelectTool} />
          ))}
        </div>
      )}

      {/* Show more toggle */}
      {rest.length > 0 && (
        <button
          onClick={() => setShowAll((v) => !v)}
          style={{
            width: '100%',
            padding: 13,
            borderRadius: 12,
            border: '1.5px dashed #d1d5db',
            background: 'transparent',
            color: '#9ca3af',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = '#8A3033';
            (e.currentTarget as HTMLButtonElement).style.color = '#8A3033';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = '#d1d5db';
            (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af';
          }}
        >
          {showAll
            ? '↑ Show fewer results'
            : `↓ Show all ${results.length} tools`}
        </button>
      )}
    </div>
  );
};
